import { useState, useEffect, useRef } from "react";
import {
  List,
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
} from "react-virtualized";

import { useConfig } from "../../context/configContext";

const cache = new CellMeasurerCache({
  fixedWidth: true,
  defaultHeight: 30,
});

// Limit memory — store only latest 5000 lines
const MAX_LOGS = 5000;

const LogsTab = ({ lognum }) => {
  const [activeTab, setActiveTab] = useState("pecc");
  const [logs, setLogs] = useState([]);
  const [autoScroll, setAutoScroll] = useState(true);
  const { config } = useConfig();
  const stationId = config.station_id;

  const listRef = useRef(null);
  const prevLognumRef = useRef(null);

  // Detect scroll position
  const handleScroll = ({ scrollHeight, scrollTop, clientHeight }) => {
    const atBottom = scrollHeight - scrollTop <= clientHeight + 20;

    if (!atBottom && autoScroll) {
      setAutoScroll(false); // User moved up => stop autoscroll
    } else if (atBottom && !autoScroll) {
      setAutoScroll(true); // User returned to bottom => resume autoscroll
    }
  };

  // Fetch logs with streaming
  useEffect(() => {
    if (activeTab !== "vsecc") return;

    // Reset logs if gun number changed
    if (prevLognumRef.current !== null && prevLognumRef.current !== lognum) {
      setLogs([]);
      cache.clearAll();
    }
    prevLognumRef.current = lognum;

    const controller = new AbortController();

    const fetchLogs = async () => {
      try {
        const token = localStorage.getItem("access_token");

        const res = await fetch(
          `/api/logs/gun/${lognum}?tab=${activeTab}&stationId=${stationId}`,
          {
            signal: controller.signal,
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        if (!res.body) return;

        const reader = res.body.getReader();
        const decoder = new TextDecoder();

        let done = false;

        while (!done) {
          const { value, done: readerDone } = await reader.read();
          done = readerDone;

          if (value) {
            const chunk = decoder.decode(value, { stream: true });

            const newLines = chunk
              .split("\n")
              .filter((line) => line.trim() !== "");

            setLogs((prev) => {
              // If autoscroll is OFF, ignore new logs (do not add)
              if (!autoScroll) return prev;

              let updated = [...prev, ...newLines];

              // Trim for memory safety
              if (updated.length > MAX_LOGS) {
                updated = updated.slice(updated.length - MAX_LOGS);
                cache.clearAll();
              }

              // Auto scroll
              if (autoScroll && listRef.current) {
                setTimeout(() => {
                  listRef.current.scrollToRow(updated.length - 1);
                }, 0);
              }

              return updated;
            });
          }
        }
      } catch (err) {
        if (err.name !== "AbortError") console.error(err);
      }
    };

    fetchLogs();
    const interval = setInterval(fetchLogs, 3000);

    return () => {
      clearInterval(interval);
      controller.abort();
    };
  }, [activeTab, lognum, autoScroll, stationId]);

  const rowRenderer = ({ index, key, style, parent }) => (
    <CellMeasurer
      key={key}
      cache={cache}
      parent={parent}
      columnIndex={0}
      rowIndex={index}
    >
      <div style={style} className="whitespace-pre-wrap break-words font-mono px-2 py-1">
        {logs[index]}
      </div>
    </CellMeasurer>
  );

  return (
    <div className="min-w-0 max-w-full overflow-x-hidden">
      <div className="flex flex-wrap gap-4">
        <button
          onClick={() => setActiveTab("pecc")}
          className={`px-4 py-2 rounded-lg ${
            activeTab === "pecc" ? "bg-blue-600 text-white" : "bg-gray-200"
          }`}
        >
          PECC
        </button>

        <button
          onClick={() => setActiveTab("vsecc")}
          className={`px-4 py-2 rounded-lg ${
            activeTab === "vsecc" ? "bg-blue-600 text-white" : "bg-gray-200"
          }`}
        >
          SECC
        </button>

        <button
          onClick={() => setAutoScroll((p) => !p)}
          className={`px-4 py-2 rounded-lg ${
            autoScroll
              ? "bg-gradient-to-r from-blue-600 to-cyan-500 text-white"
              : "bg-gray-400 text-black"
          }`}
        >
          Auto Scroll: {autoScroll ? "ON" : "OFF"}
        </button>
      </div>

      <h2 className="mt-4 font-semibold text-lg">Active Tab: {activeTab}</h2>

      <div className="mt-4 h-[calc(70vh-40px)] min-w-0 max-w-full overflow-hidden rounded-xl border border-gray-300 bg-black text-white sm:h-[calc(90vh-10px)] md:h-[calc(82vh-5px)] lg:h-[calc(88vh-50px)]">
        <AutoSizer>
          {({ height, width }) => (
            <List
              ref={listRef}
              width={width}
              height={height}
              rowCount={logs.length}
              deferredMeasurementCache={cache}
              rowHeight={cache.rowHeight}
              rowRenderer={rowRenderer}
              onScroll={handleScroll}
              overscanRowCount={10}
            />
          )}
        </AutoSizer>
      </div>
    </div>
  );
};

export default LogsTab;
